/**
 * Agent secret middleware tags (Client-safe)
 *
 * Agent-facing endpoints authenticate via a shared secret (AGENT_SECRET).
 * This file contains only the tag declarations — no server dependencies.
 *
 * @since 1.0.0
 */
import { RpcMiddleware } from "@effect/rpc";
import { Context, Schema } from "effect";

import { Unauthorized } from "./errors.ts";

// ============================================================================
// Context Tags
// ============================================================================

/**
 * Context provided by the agent secret middleware.
 *
 * `installationId` is sourced from the verified `x-installation-id`
 * header — handlers should prefer this over any caller-asserted
 * installation id in the request payload (closes the prior gap where
 * an authenticated agent could spoof any other install's id in the
 * RPC body).
 *
 * @since 1.0.0
 */
export class AgentIdentity extends Context.Tag("AgentIdentity")<
  AgentIdentity,
  { readonly verified: true; readonly installationId: string }
>() {}

// ============================================================================
// Middleware Tag
// ============================================================================

/**
 * Agent secret authentication middleware tag.
 *
 * Verifies the `x-installation-id` + `x-agent-secret` headers against
 * the `agent_installations.agentSecret` column for that installation.
 *
 * @since 1.0.0
 */
export class AgentSecretMiddleware extends RpcMiddleware.Tag<AgentSecretMiddleware>()(
  "AgentSecretMiddleware",
  {
    provides: AgentIdentity,
    failure: Schema.Union(Unauthorized),
  },
) {}
